<template>
  <div
    class="action_column"
    :class="{ border_right: !isLastColumn }"
    :style="{ minWidth: columnWidth }"
  >
    <div class="action_content">
      <template v-if="showCustomActionComponent">
        <component
          class="mr-6"
          :is="customActionComponent"
          :row="row"
          :custom-action-key="customActionKey"
        />
      </template>

      <div v-if="quickActions?.length > 0" class="quick_actions_wrapper">
        <template v-if="useProspectTableActions">
          <template
            v-for="(action, actionIndex) in quickActions"
            :key="actionIndex"
          >
            <ProspectTableActions
              v-if="prospectTypeFor(action.id)"
              :type="prospectTypeFor(action.id)"
              :entity-id="row.id"
              :entity-type="source"
              :value="prospectValueFor(action.id)"
            />
          </template>
        </template>

        <template v-else>
          <template
            v-for="(action, actionIndex) in quickActions"
            :key="actionIndex"
          >
            <EmailQuickAction
              v-if="action.id === quickActionsTypes.EMAIL"
              :row="row"
              :source="source"
              :from-prospect="fromProspect"
              :action="action"
              @action="action.handler"
            />

            <CallQuickAction
              v-if="action.id === quickActionsTypes.CALL"
              :row="row"
              :from-prospect="fromProspect"
              :source="source"
              :action="action"
            />

            <ListQuickAction
              v-if="action.id === quickActionsTypes.ADD_TO_LIST"
              :row="row"
              :source="source"
              :entity-lists="entityLists"
              :has-list-access="hasListAccess"
              @entity-list-created="handleEntityList"
              @list-create="handleListCreate"
            />

            <SequenceQuickAction
              v-if="action.id === quickActionsTypes.ADD_TO_SEQUENCE"
              :row="row"
              :sequences="sequences"
              :modelValue="modelValue"
              @update:modelValue="(value) => $emit('update:modelValue', value)"
              :is-sequence-enabled="isSequenceEnabled"
              :has-sequence-access="hasSequenceAccess"
              @sequence-created="handleSequence"
              @sequence-create="handleSequenceCreate"
            />

            <MarketResearchQuickAction
              v-if="action.id === quickActionsTypes.MARKET_RESEARCH"
              :row="row"
              :source="source"
              :pendingIds="pendingIds"
            />
          </template>
        </template>
      </div>

      <CustomDropdown
        v-if="filteredActions?.length > 0"
        gap="4px"
        position="end"
        :relative-to-viewport="true"
        :disabled="row[isMoreOptionsDisabled]"
      >
        <template #trigger>
          <div class="quick_action_item mt-1">
            <ThemeImage
              alt="more_icon"
              class="size-5 cursor-pointer opacity-60"
              :icon="imageNames.MORE_VERTICAL"
            />
          </div>
        </template>

        <template #default>
          <div
            v-for="(action, actionIndex) in filteredActions"
            :key="actionIndex"
            @click="handleAction(action, row)"
            class="dropdown_item text-sm"
            :class="{ 'action_loading': getActionLoading(action) }"
          >
            <img
              v-if="action.imgSrc"
              :src="action.imgSrc"
              alt="action_option"
              class="w-3 h-3"
            />
            <ThemeImage
              v-else-if="getActionLabel(action) === 'Add Action'"
              :icon="action.icon"
              alt="action_option"
            />
            <ThemeImage
              v-else-if="!getActionLoading(action)"
              class="size-3"
              :icon="action.icon"
              alt="action_option"
            />
            <CustomLoader v-else :size="12" :show-label="false" />
            {{ getActionLabel(action) }}
          </div>
        </template>
      </CustomDropdown>
    </div>
  </div>
</template>

<script setup>
import { computed, inject } from "vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import EmailQuickAction from "@/src/components/custom_table_components/EmailQuickAction.vue";
import CallQuickAction from "@/src/components/custom_table_components/CallQuickAction.vue";
import ListQuickAction from "@/src/components/custom_table_components/ListQuickAction.vue";
import SequenceQuickAction from "@/src/components/custom_table_components/SequenceQuickAction.vue";
import ProspectTableActions from "@/src/components/custom_table_components/ProspectTableActions.vue";
import { quickActionsTypes } from "@/src/constants/componentConstants.js";
import MarketResearchQuickAction from "@/src/components/custom_table_components/MarketResearchQuickAction.vue";
import store from "@/src/store/index.js";
import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  row: { type: Object, required: true },
  rowIndex: { type: Number, required: true },
  actions: { type: Array, default: () => [] },
  quickActions: { type: Array, default: () => [] },
  source: { type: String, required: true },
  entityLists: { type: Array, default: () => [] },
  sequences: { type: Array, default: () => [] },
  modelValue: { type: String, required: true },
  isSequenceEnabled: { type: Boolean, default: false },
  columnWidth: { type: String, required: true },
  isLastColumn: { type: Boolean, default: false },
  fromProspect: { type: Boolean, required: false, default: false },
  dropdownIndex: { type: Number, default: null },
  dropdownPosition: { type: Object, default: () => ({ top: 0, left: 0 }) },
  customActionKey: { type: String, required: false },
  customActionComponent: { type: Object, default: null },
  isMoreOptionsDisabled: { type: String, required: false },
  prospectData: { type: Boolean, default: false, required: false },
  pendingIds: { type: Array, default: () => [] },
});

const emit = defineEmits([
  "action",
  "entity-list-created",
  "sequence-created",
  "entity-list-created-apollo",
  "sequence-created-apollo",
  "list-create",
  "sequence-create",
  "update:modelValue",
]);
const hasListAccess = computed(() => store.getters["rbac/hasListsAccess"]);
const hasSequenceAccess = computed(
  () => store.getters["rbac/hasSequencesAccess"],
);

const prospectTableActionsHandlers = inject("prospectTableActionsHandlers", null);
const useProspectTableActions = computed(() => !!prospectTableActionsHandlers);

const prospectTypeMap = {
  [quickActionsTypes.EMAIL]: "email",
  [quickActionsTypes.CALL]: "phone",
  [quickActionsTypes.ADD_TO_LIST]: "list",
  [quickActionsTypes.ADD_TO_SEQUENCE]: "sequence",
};

const prospectTypeFor = (id) => prospectTypeMap[id];

const prospectValueFor = (id) => {
  if (id === quickActionsTypes.EMAIL) return props.row.email || "";
  if (id === quickActionsTypes.CALL) return props.row.phone_number || "";
  return "";
};

const filteredActions = computed(() => {
  return props.actions.filter((action) => {
    if (action.condition && typeof action.condition === "function") {
      return action.condition(props.row);
    }
    return true;
  });
});

const showCustomActionComponent = computed(() => {
  return props.customActionComponent && props.customActionKey;
});

const handleAction = (action, row) => {
  if (getActionLoading(action)) {
    return;
  }
  const excludedActions = ["call", "add_to_list", "add_to_sequence"];
  if (!excludedActions.includes(action.id)) {
    emit("action", { action, row });
  }
};

const handleEntityList = (payload) => {
  if (props.prospectData) {
    emit("entity-list-created-apollo", payload);
  } else {
    emit("entity-list-created", payload);
  }
};

const handleSequence = (payload) => {
  if (props.prospectData) {
    emit("sequence-created-apollo", payload);
  } else {
    emit("sequence-created", payload);
  }
};

const handleListCreate = (row) => {
  emit("list-create", row);
};

const handleSequenceCreate = (row) => {
  emit("sequence-create", row);
};

const getActionLabel = (action) => {
  if (typeof action.label === 'function') {
    return action.label(props.row);
  }
  return action.label;
};

const getActionLoading = (action) => {
  if (typeof action.loading === 'function') {
    return action.loading(props.row);
  }
  return action.loading || false;
};
</script>

<style scoped>
.action_column {
  background-color: transparent;
  display: flex;
  flex-direction: column;
}

.action_content {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid var(--white-opacity-12);
  box-sizing: border-box;
}

.quick_actions_wrapper {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  flex-shrink: 0;
}

.border_right {
  border-right: 1px solid var(--white-opacity-12);
}

.dropdown_item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.dropdown_item:hover {
  background-color: var(--white-opacity-4);
}

.action_loading {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}
</style>
